/**
 * WebRTC 最终选中的候选路径。
 *
 * host  = 同网段/可直接访问的地址
 * srflx = 通过 STUN 得到的公网映射，仍然是端到端直连
 * prflx = ICE 在连通性检查中发现的对等反射地址，仍然是端到端直连
 * relay = TURN 中继，媒体和 DataChannel 会经过 TURN 服务
 */
export type WebRtcPathKind = 'direct' | 'stun' | 'turn' | 'unknown';

export interface WebRtcConnectionPath {
  kind: WebRtcPathKind;
  label: string;
  detail: string;
  p2p: boolean | null;
}

interface StatsRecord {
  type?: string;
  id?: string;
  state?: string;
  selected?: boolean;
  nominated?: boolean;
  selectedCandidatePairId?: string;
  localCandidateId?: string;
  remoteCandidateId?: string;
  candidateType?: string;
  protocol?: string;
  kind?: string;
  mediaType?: string;
  currentRoundTripTime?: number;
  availableOutgoingBitrate?: number;
  bytesSent?: number;
  bytesReceived?: number;
  jitter?: number;
  packetsLost?: number;
  packetsReceived?: number;
  framesPerSecond?: number;
  framesDropped?: number;
  framesDecoded?: number;
  frameWidth?: number;
  frameHeight?: number;
  decoderImplementation?: string;
  encoderImplementation?: string;
  codecId?: string;
  mimeType?: string;
  jitterBufferDelay?: number;
  jitterBufferEmittedCount?: number;
  qualityLimitationReason?: string;
}

const UNKNOWN_PATH: WebRtcConnectionPath = {
  kind: 'unknown',
  label: '正在连接',
  detail: '正在建立安全连接',
  p2p: null,
};

export interface WebRtcConnectionMetrics {
  rttMs: number | null;
  /** 控制通道最近一次心跳的真实往返时间，使用同一端时间戳计算。 */
  controlRttMs?: number | null;
  jitterMs: number | null;
  packetLossPercent: number | null;
  framesPerSecond: number | null;
  framesDropped: number | null;
  framesDecoded: number | null;
  jitterBufferMs: number | null;
  availableOutgoingBitrateKbps: number | null;
  qualityLimitationReason: string | null;
  controlBufferedAmount: number | null;
  /** 当前端点观测到的 WebRTC 上行吞吐，单位 Kbps。 */
  uploadKbps?: number | null;
  /** 当前端点观测到的 WebRTC 下行吞吐，单位 Kbps。 */
  downloadKbps?: number | null;
  /** 当前视频流实际传输/解码的画面宽高；不是用户选择的目标值。 */
  videoWidth?: number | null;
  videoHeight?: number | null;
  /** 仅用于诊断，不直接展示底层网络地址。 */
  videoCodec?: string | null;
  videoImplementation?: string | null;
}

export interface WebRtcTrafficSample {
  timestamp: number;
  bytesSent: number | null;
  bytesReceived: number | null;
}

const EMPTY_METRICS: WebRtcConnectionMetrics = {
  rttMs: null,
  jitterMs: null,
  packetLossPercent: null,
  framesPerSecond: null,
  framesDropped: null,
  framesDecoded: null,
  jitterBufferMs: null,
  availableOutgoingBitrateKbps: null,
  qualityLimitationReason: null,
  controlBufferedAmount: null,
  videoWidth: null,
  videoHeight: null,
  videoCodec: null,
  videoImplementation: null,
};

export function pendingWebRtcPath(label = '正在连接…'): WebRtcConnectionPath {
  return {
    kind: 'unknown',
    label,
    detail: '正在建立安全连接',
    p2p: null,
  };
}

export function disconnectedWebRtcPath(): WebRtcConnectionPath {
  return {
    ...UNKNOWN_PATH,
    label: '未连接',
    detail: '连接尚未建立',
  };
}

function candidateType(stats: StatsRecord | undefined): string {
  return typeof stats?.candidateType === 'string' ? stats.candidateType : '';
}

function describePath(local: string, remote: string, _protocol: string): WebRtcConnectionPath {
  const types = [local, remote];
  const relay = types.includes('relay');
  const stun = types.some((type) => type === 'srflx' || type === 'prflx');

  if (relay) {
    return {
      kind: 'turn',
      label: 'TURN 中继',
      detail: '媒体与控制数据经过 TURN 中继服务器传输',
      p2p: false,
    };
  }
  if (stun) {
    return {
      kind: 'stun',
      label: 'P2P 穿透',
      detail: '通过 STUN 建立端到端加密连接，媒体与控制不经过中央服务器',
      p2p: true,
    };
  }
  if (types.some((type) => type === 'host')) {
    return {
      kind: 'direct',
      label: 'P2P 直连',
      detail: '端到端加密直连，媒体与控制不经过中央服务器',
      p2p: true,
    };
  }
  return {
    kind: 'unknown',
    label: '连接已建立',
    detail: '设备已连接',
    p2p: null,
  };
}

function selectedCandidatePair(stats: StatsRecord[]): StatsRecord | undefined {
  const byId = new Map(stats.filter((item) => item.id).map((item) => [item.id!, item]));
  const transport = stats.find(
    (item) => item.type === 'transport' && typeof item.selectedCandidatePairId === 'string',
  );
  if (transport?.selectedCandidatePairId) {
    return byId.get(transport.selectedCandidatePairId);
  }

  // 某些 Chromium 版本没有 transport.selectedCandidatePairId，优先使用明确标记的候选对。
  // 如果连 selected/nominated 都没有，就返回未知，不能拿“任意 succeeded”冒充当前路径。
  return stats.find(
    (item) =>
      item.type === 'candidate-pair' &&
      item.state === 'succeeded' &&
      (item.selected === true || item.nominated === true),
  );
}

function isVideoRtp(item: StatsRecord): boolean {
  return item.type === 'inbound-rtp' || item.type === 'outbound-rtp'
    ? item.kind === 'video' || item.mediaType === 'video'
    : false;
}

function byteCounter(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null;
}

function sumByteCounters(stats: StatsRecord[], type: string, field: 'bytesSent' | 'bytesReceived'): number | null {
  let total = 0;
  let found = false;
  for (const item of stats) {
    if (item.type !== type) continue;
    const value = byteCounter(item[field]);
    if (value == null) continue;
    total += value;
    found = true;
  }
  return found ? total : null;
}

function trafficFromStats(stats: StatsRecord[], pair: StatsRecord | undefined, timestamp: number): WebRtcTrafficSample {
  return {
    timestamp,
    // candidate-pair is the preferred source because it includes the complete
    // WebRTC transport (media and DataChannels). RTP is a compatibility fallback
    // for Chromium/Electron versions that do not expose pair byte counters.
    bytesSent: byteCounter(pair?.bytesSent) ?? sumByteCounters(stats, 'outbound-rtp', 'bytesSent'),
    bytesReceived: byteCounter(pair?.bytesReceived) ?? sumByteCounters(stats, 'inbound-rtp', 'bytesReceived'),
  };
}

function trafficRateKbps(
  previous: number | null,
  current: number | null,
  elapsedSeconds: number,
): number | null {
  if (previous == null || current == null || elapsedSeconds <= 0 || current < previous) return null;
  return ((current - previous) * 8) / elapsedSeconds / 1000;
}

function frameDimension(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? Math.round(value)
    : null;
}

function videoCodecName(stats: StatsRecord[], video: StatsRecord | undefined): string | null {
  if (!video?.codecId) return null;
  const codec = stats.find((item) => item.type === 'codec' && item.id === video.codecId);
  if (typeof codec?.mimeType !== 'string') return null;
  const mimeType = codec.mimeType.trim();
  if (!/^video\//i.test(mimeType)) return null;
  return mimeType.slice('video/'.length).toUpperCase() || null;
}

function metricsFromStats(
  stats: StatsRecord[],
  pair: StatsRecord | undefined,
  controlBufferedAmount?: number,
  traffic?: WebRtcTrafficSample,
  previousTraffic?: WebRtcTrafficSample | null,
  controlRttMs?: number | null,
): WebRtcConnectionMetrics {
  const inboundVideo = stats.find((item) => item.type === 'inbound-rtp' && isVideoRtp(item));
  const outboundVideo = stats.find((item) => item.type === 'outbound-rtp' && isVideoRtp(item));
  // 控制端通常只有 inbound-rtp，被控端通常只有 outbound-rtp。优先使用
  // inbound 的实际解码尺寸；两端都暴露时也不会用“目标设置”冒充真实画面。
  const video = inboundVideo || outboundVideo;
  const packetsReceived = inboundVideo?.packetsReceived;
  const packetsLost = inboundVideo?.packetsLost;
  const packetTotal = typeof packetsReceived === 'number' && typeof packetsLost === 'number'
    ? packetsReceived + packetsLost
    : 0;
  const jitterBufferMs = inboundVideo &&
    typeof inboundVideo.jitterBufferDelay === 'number' &&
    typeof inboundVideo.jitterBufferEmittedCount === 'number' &&
    inboundVideo.jitterBufferEmittedCount > 0
    ? (inboundVideo.jitterBufferDelay / inboundVideo.jitterBufferEmittedCount) * 1000
    : null;
  const elapsedSeconds = previousTraffic && traffic
    ? (traffic.timestamp - previousTraffic.timestamp) / 1000
    : 0;

  return {
    rttMs: typeof pair?.currentRoundTripTime === 'number'
      ? pair.currentRoundTripTime * 1000
      : null,
    controlRttMs: controlRttMs ?? null,
    jitterMs: typeof inboundVideo?.jitter === 'number' ? inboundVideo.jitter * 1000 : null,
    packetLossPercent: packetTotal > 0 ? (packetsLost! / packetTotal) * 100 : null,
    framesPerSecond: typeof inboundVideo?.framesPerSecond === 'number'
      ? inboundVideo.framesPerSecond
      : typeof outboundVideo?.framesPerSecond === 'number'
        ? outboundVideo.framesPerSecond
        : null,
    framesDropped: typeof inboundVideo?.framesDropped === 'number' ? inboundVideo.framesDropped : null,
    framesDecoded: typeof inboundVideo?.framesDecoded === 'number' ? inboundVideo.framesDecoded : null,
    jitterBufferMs,
    availableOutgoingBitrateKbps: typeof pair?.availableOutgoingBitrate === 'number'
      ? pair.availableOutgoingBitrate / 1000
      : null,
    qualityLimitationReason: outboundVideo?.qualityLimitationReason && outboundVideo.qualityLimitationReason !== 'none'
      ? outboundVideo.qualityLimitationReason
      : null,
    controlBufferedAmount: typeof controlBufferedAmount === 'number' ? controlBufferedAmount : null,
    uploadKbps: traffic
      ? trafficRateKbps(previousTraffic?.bytesSent ?? null, traffic.bytesSent, elapsedSeconds)
      : null,
    downloadKbps: traffic
      ? trafficRateKbps(previousTraffic?.bytesReceived ?? null, traffic.bytesReceived, elapsedSeconds)
      : null,
    videoWidth: frameDimension(inboundVideo?.frameWidth ?? outboundVideo?.frameWidth),
    videoHeight: frameDimension(inboundVideo?.frameHeight ?? outboundVideo?.frameHeight),
    videoCodec: videoCodecName(stats, video),
    videoImplementation: inboundVideo?.decoderImplementation || outboundVideo?.encoderImplementation || null,
  };
}

function pathFromStats(stats: StatsRecord[], pair: StatsRecord | undefined): WebRtcConnectionPath {
  if (!pair) return UNKNOWN_PATH;
  const byId = new Map(stats.filter((item) => item.id).map((item) => [item.id!, item]));
  const local = pair.localCandidateId ? byId.get(pair.localCandidateId) : undefined;
  const remote = pair.remoteCandidateId ? byId.get(pair.remoteCandidateId) : undefined;
  return describePath(candidateType(local), candidateType(remote), pair.protocol || '');
}

export interface WebRtcConnectionSnapshot {
  path: WebRtcConnectionPath;
  metrics: WebRtcConnectionMetrics;
  traffic: WebRtcTrafficSample;
}

export async function getWebRtcConnectionSnapshot(
  pc: RTCPeerConnection,
  controlBufferedAmount?: number,
  previousTraffic?: WebRtcTrafficSample | null,
  controlRttMs?: number | null,
): Promise<WebRtcConnectionSnapshot> {
  try {
    const report = await pc.getStats();
    const stats = Array.from(report.values()) as unknown as StatsRecord[];
    const pair = selectedCandidatePair(stats);
    const traffic = trafficFromStats(stats, pair, Date.now());
    return {
      path: pathFromStats(stats, pair),
      metrics: metricsFromStats(stats, pair, controlBufferedAmount, traffic, previousTraffic, controlRttMs),
      traffic,
    };
  } catch {
    return {
      path: UNKNOWN_PATH,
      metrics: { ...EMPTY_METRICS, controlBufferedAmount: controlBufferedAmount ?? null, controlRttMs: controlRttMs ?? null },
      traffic: { timestamp: Date.now(), bytesSent: null, bytesReceived: null },
    };
  }
}

/**
 * 从浏览器原生统计中读取当前真正被选中的 ICE Candidate Pair。
 * 只返回候选类型和传输协议，不暴露双方 IP，避免把网络地址写入界面或日志。
 */
export async function getWebRtcConnectionPath(
  pc: RTCPeerConnection,
): Promise<WebRtcConnectionPath> {
  return (await getWebRtcConnectionSnapshot(pc)).path;
}

export async function getWebRtcConnectionMetrics(
  pc: RTCPeerConnection,
  controlBufferedAmount?: number,
  controlRttMs?: number | null,
): Promise<WebRtcConnectionMetrics> {
  return (await getWebRtcConnectionSnapshot(pc, controlBufferedAmount, undefined, controlRttMs)).metrics;
}

export function formatWebRtcConnectionMetrics(metrics: WebRtcConnectionMetrics): string {
  const observed = [
    metrics.rttMs,
    metrics.controlRttMs,
    metrics.jitterMs,
    metrics.packetLossPercent,
    metrics.framesPerSecond,
    metrics.videoWidth,
    metrics.videoHeight,
  ].some((value) => value != null);
  if (!observed) return '正在检查';

  const slow =
    (metrics.controlRttMs != null && metrics.controlRttMs >= 350) ||
    (metrics.controlRttMs == null && metrics.rttMs != null && metrics.rttMs >= 350) ||
    (metrics.rttMs != null && metrics.rttMs >= 800) ||
    (metrics.packetLossPercent != null && metrics.packetLossPercent >= 8) ||
    (metrics.jitterMs != null && metrics.jitterMs >= 120) ||
    (metrics.controlBufferedAmount != null && metrics.controlBufferedAmount >= 256 * 1024);
  if (slow) return '连接较慢';

  const fair =
    (metrics.controlRttMs != null && metrics.controlRttMs >= 180) ||
    (metrics.controlRttMs == null && metrics.rttMs != null && metrics.rttMs >= 180) ||
    (metrics.rttMs != null && metrics.rttMs >= 450) ||
    (metrics.packetLossPercent != null && metrics.packetLossPercent >= 3) ||
    (metrics.jitterMs != null && metrics.jitterMs >= 70);
  return fair ? '连接一般' : '连接顺畅';
}

export function formatWebRtcRate(kbps: number | null | undefined): string {
  if (kbps == null || !Number.isFinite(kbps) || kbps < 0) return '—';
  const bytesPerSecond = (kbps * 1000) / 8;
  if (bytesPerSecond < 1024) return `${Math.round(bytesPerSecond)} B/s`;
  if (bytesPerSecond < 1024 * 1024) {
    const value = bytesPerSecond / 1024;
    return `${value >= 10 ? value.toFixed(0) : value.toFixed(1)} KB/s`;
  }
  const value = bytesPerSecond / (1024 * 1024);
  return `${value >= 10 ? value.toFixed(0) : value.toFixed(1)} MB/s`;
}

export function formatWebRtcTraffic(metrics: WebRtcConnectionMetrics): string {
  return `↑ ${formatWebRtcRate(metrics.uploadKbps)} · ↓ ${formatWebRtcRate(metrics.downloadKbps)}`;
}

export function formatWebRtcLiveStats(metrics: WebRtcConnectionMetrics): string {
  const fps = metrics.framesPerSecond == null || !Number.isFinite(metrics.framesPerSecond)
    ? '—'
    : String(Math.max(0, Math.round(metrics.framesPerSecond)));
  return `${formatWebRtcTraffic(metrics)} · ${fps} FPS`;
}

export function formatWebRtcMilliseconds(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value) || value < 0) return '检测中';
  return `${Math.round(value)} ms`;
}

export function formatWebRtcPercent(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value) || value < 0) return '检测中';
  return `${value >= 10 ? value.toFixed(0) : value.toFixed(1)}%`;
}

export function formatWebRtcFps(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value) || value < 0) return '检测中';
  return `${Math.max(0, Math.round(value))} FPS`;
}

export function formatWebRtcResolution(
  width: number | null | undefined,
  height: number | null | undefined,
): string {
  if (
    width == null || height == null ||
    !Number.isFinite(width) || !Number.isFinite(height) ||
    width <= 0 || height <= 0
  ) return '检测中';
  return `${Math.round(width)} × ${Math.round(height)}`;
}
